import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/browser';
import * as R from 'ramda';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { concatMap, filter, finalize, first, map, shareReplay, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { APIService, KPI } from '../api';
import { YearsService } from './years.service';
import { AssetsService } from './assets.service';
import { BuildingsService } from './buildings.service';
import { DocumentTypeRequirementState, DocumentTypesService } from './document-types.service';
import { ImportsService } from './imports.service';
import { InvestmentCategoriesService } from './investment-categories.service';
import { InvestmentExecutionsService } from './investment-executions.service';
import { InvestmentReasonsService } from './investment-reasons.service';
import { PerimetersService } from './perimeters.service';
import { ReferenceDataService } from './reference-data.service';
import { idMapToArray, ScopeService } from './scope.service';
import { UtilsService } from './utils.service';

interface MappedInvestmentDocumentsMap {
  requirement_state: DocumentTypeRequirementState;
  required: boolean; // `true` if `MANDATORY`, `false` otherwise (`OPTIONAL` or no requirement)
  has_attached: boolean;
}

export interface MappedInvestmentDocuments extends KPI.MappedInvestment {
  /**
   * Maps every documents type by their id to an object
   * describing the status of this document type for this investment.
   */
  document_types_map: {
    [document_type_id: number]: MappedInvestmentDocumentsMap;
  };
}

@Injectable()
export class InvestmentsService {
  /** Investments loading state stream. */
  public investmentsLoading$ = new BehaviorSubject<boolean>(false);
  /** Reload 1 investment */
  public reloadInvestment$ = new BehaviorSubject<number>(0);
  public newInvestmentCreated$ = new BehaviorSubject<KPI.Investment>(null);
  /** Emitted when investment links change (link/unlink) — triggers full grid re-render */
  public investmentLinksChanged$ = new Subject<void>();

  /** Investments stream for alternate scenarii. Subscribed on demand. */
  private investmentsByScenario$: {
    [scenario: number]: Observable<MappedInvestmentDocuments[]>;
  } = {};
  private forceReload$ = new BehaviorSubject<void>(null);
  /** Investments stream for main scenario. Subscribed by default. */
  private _investments$: Observable<MappedInvestmentDocuments[]>;
  /** Cache of investments per year and building */
  private investmentsPerScenarioYearBuilding: {
    [scenario: number]: {
      [building: number]: MappedInvestmentDocuments[];
    };
  } = {};
  /** Cache of investments per year and building */
  private loadedYearBuilding: {
    [scenario: number]: {
      [year: number]: {
        [building: number]: boolean;
      };
    };
  } = {};

  constructor(
    private api: APIService,
    private assetsService: AssetsService,
    private buildingsService: BuildingsService,
    private documentTypesService: DocumentTypesService,
    private importsService: ImportsService,
    private investmentCategoriesService: InvestmentCategoriesService,
    private investmentExecutionService: InvestmentExecutionsService,
    private investmentReasonsService: InvestmentReasonsService,
    private perimetersService: PerimetersService,
    private referenceDataService: ReferenceDataService,
    private scope: ScopeService,
    private translate: TranslateService,
    private years: YearsService,
    private utils: UtilsService,
  ) { }

  /**
   * @desc Compute the total of slices whith same status as investment
   * @note Older statuses should not been computed - only used to have the history of the slice
   */
  public static getInvestmentTotalPrice(
    investment: KPI.Investment,
    startYear?: number,
    endYear?: number,
  ): number {
    return investment.slices
      .filter((s) => s.status === investment.status)
      .reduce((total, slice) => {
        if (
          (!startYear || slice.year >= startYear) &&
          (!endYear || slice.year <= endYear)
        ) {
          let tot: number = total + <number>slice.price;
          if (slice.additional_price) {
            tot = tot + <number>slice.additional_price;
          }
          return tot;
        }
        return total;
      }, 0);
  }

  public static getScoppedSlices(
    investment: KPI.Investment,
    startYear?: number,
    endYear?: number,
  ) {
    return investment.slices.filter(
      (s) =>
        (!startYear || s.year >= startYear) && (!endYear || s.year <= endYear),
    );
  }

  public static getInvestmentSpentRemaining(
    investment: KPI.Investment,
    startYear?: number,
    endYear?: number,
  ): number[] {
    return investment.spending_slices.reduce(
      ([spent, remaining], spendingSlice) => {
        if (
          (!startYear || spendingSlice.year >= startYear) &&
          (!endYear || spendingSlice.year <= endYear)
        ) {
          return [
            spent + <number>spendingSlice.spent,
            remaining + <number>spendingSlice.remaining,
          ];
        }
        return [spent, remaining];
      },
      [0, 0],
    );
  }

  private _disableGlobalPreload = false;

  public setDisableGlobalPreload(disable: boolean): void {
    this._disableGlobalPreload = disable;
  }

  /** @deprecated
   * DO NOT USE THIS!
   */
  public investments$(forcedYears?: number[], assetFields?: string): Observable<MappedInvestmentDocuments[]> {
    if (this._disableGlobalPreload) {
      return of([]);
    }

    if (!this._investments$ || assetFields) {
      this._investments$ = this.getInvestments(null, forcedYears, assetFields);
    }

    return this._investments$;
  }

  /**
   *
   * @param scenario
   * @returns Every investments from the specified scenario
   */
  public getInvestmentsByScenario$(
    scenario: KPI.InvestmentScenario,
    assetFields?: string,
  ): Observable<MappedInvestmentDocuments[]> {
    if (!this.investmentsByScenario$[scenario.id]) {
      this.investmentsByScenario$[scenario.id] = this.getInvestments(
        scenario.id,
        null,
        assetFields,
      );
    }
    return this.investmentsByScenario$[scenario.id];
  }

  /**
   * Forces to reload investments from server.
   */
  public reloadInvestments(): void {
    this.investmentsPerScenarioYearBuilding = {};
    this.loadedYearBuilding = {};
    this.forceReload$.next(null);
  }


  /**
   * Gets a single investment by id.
   *
   * @param id The id. of the investment to retrieve.
   * @param minimal: deprecated, we no longer use this
   */
  public getInvestment(
    id: number,
    // @ts-ignore
    minimal?: boolean=false, // deprecated. No longer used
  ): Observable<MappedInvestmentDocuments | KPI.Investment> {
    if (!id) {
      return of(null);
    }
    const getInvestmentObservable = this.api.investments_investments_get(id).pipe(
      concatMap((investment) =>
        (investment.asset
          ? this.assetsService.getAsset(investment.asset)
          : of(null as KPI.MappedAsset)
        ).pipe(map((asset) => ({ investment, asset }))),
      ),
      concatMap(({ investment, asset }) =>  this.mapInvestments([investment], [asset]),
      ),
      map(([investment]) => investment),
    );

    return getInvestmentObservable;
  }

  public getInvestmentsByIds(ids: number[]): Observable<KPI.Investment[]> {
    return this.api.investments_by_ids_get(ids);
  }

  public sendInvestmentEmail(
    investmentId: number,
    emails: string[],
    message: string
  ): Observable<{ notified_emails: string[] }> {
    return this.api.investment_notify(investmentId, emails, message);
  }

  /**
   * Always fetch an investment directly from the raw API (no mapInvestments, no cache).
   * Use this when you need fields like links_from that are stripped by mapInvestments.
   */
  public getInvestmentRaw(id: number): Observable<KPI.Investment | null> {
    if (!id) return of(null);
    return this.api.investments_investments_get(id) as Observable<KPI.Investment>;
  }

  /**
   * Get an investment from the cache if available (fully mapped), otherwise fetch raw from the API
   * without going through mapInvestments. Avoids hanging combineLatest chains in panel contexts.
   */
  public getInvestmentCacheOrRaw(id: number): Observable<KPI.Investment> {
    if (!id) return of(null);
    return this.investments$().pipe(
      first(),
      map(investments => investments.find(inv => inv.id === id)),
      switchMap(cached => cached !== undefined
        ? of(cached as KPI.Investment)
        : (this.api.investments_investments_get(id) as Observable<KPI.Investment>)
      ),
    );
  }

  /**
   * Delete an investment
   * @param id
   */
  public deleteInvestment(id: number): Observable<void> {
    return this.api
      .investments_investment_delete(id)
  }

  /**
   * Load investments using params and then map the results
   * @param params
   */
  public loadInvestmentWithParams(
    params?: any,
    fields?: string,
    minimal?: boolean,
  ): Observable<MappedInvestmentDocuments[] | KPI.Investment[]> {
    return this.api.investments_investments_ex(params).pipe(
      concatMap((investments) => {
        if (minimal) {
          return this.mapMinimalInvestments(investments);
        }
        // Filter nil values because orphan investments don't have an asset
        const assetsIds = R.filter(
          R.complement(R.isNil),
          R.uniq(R.pluck('asset', investments)),
        );
        return this.assetsService
          .getAssets(assetsIds, fields)
          .pipe(concatMap((assets) => this.mapInvestments(investments, assets)));
      }),
    );
  }

  public getLinkedInvestments(id: number): Observable<MappedInvestmentDocuments[]> {
    return this.api.investments_investments_get(id).pipe(
      concatMap((investment: any) => {
        const linkedIds = (investment.links_from || []).map((link: any) =>
          typeof link === 'object' ? link.id : link
        );

        if (linkedIds.length === 0) {
          return of([] as KPI.Investment[]);
        }

        const linkedInvestments$ = linkedIds.map((linkedId: number) =>
          this.api.investments_investments_get(linkedId)
        );

        return combineLatest(linkedInvestments$);
      }),
      concatMap((investments: KPI.Investment[]) => {
        if (investments.length === 0) {
          return of({ investments: [] as KPI.Investment[], assets: [] as KPI.MappedAsset[] });
        }

        const assetsIds = R.filter(
          R.complement(R.isNil),
          R.uniq(R.pluck('asset', investments)),
        );
        return this.assetsService
          .getAssets(assetsIds)
          .pipe(map((assets) => ({ investments, assets })));
      }),
      concatMap(({ investments, assets }) =>
        investments.length > 0 ? this.mapInvestments(investments, assets) : of([])
      ),
    );
  }

  public unlinkInvestment(id: number): Observable<void> {
    return this.updateInvestment(id, { linked_to: null }).pipe(
      map(() => void 0)
    );
  }

  /**
   * Get the investment pictures
   *
   * @param investment
   */
  public getInvestmentPictures(
    investment: KPI.Investment,
  ): Observable<KPI.InvestmentPicture[]> {
    return this.api.investments_investments_pictures({
      investments: investment.id,
    });
  }

  /**
   * Updates an investment in the data base
   *
   * @param id
   * @param investment
   */
  public updateInvestment(
    id: number,
    updatedInvestment: Partial<KPI.UpdateInvestment>,
  ): Observable<KPI.MappedInvestment> {
    return this.api.investments_investments_patch(id, updatedInvestment).pipe(
      concatMap((investment) => {
        if (investment.asset) {
          return this.assetsService
            .getAsset(investment.asset)
            .pipe(map((asset) => ({ investment, asset })));
        } else {
          return [{ investment, asset: null as KPI.MappedAsset }];
        }
      }),
      concatMap(({ investment, asset }) =>
        this.mapInvestments([investment], [asset]),
      ),
      map(([investment]) => investment),
    );
  }

  public linkInvestment(id: number, mainInvestmentId: number): Observable<any> {
    return this.updateInvestment(id, { linked_to: mainInvestmentId });
  }

  public getInvestmentTotal(
    yearFrom: number,
    yearTo: number,
    buildingIds: number[],
  ): Observable<KPI.InvestmentTotal> {
    return this.api.investments_investment_total({
      year_from: yearFrom,
      year_to: yearTo,
      buildings: buildingIds.join(','),
    });
  }

  private getInvestments(
    scenario?: number,
    forcedYears?: number[],
    assetFields?: string,
  ): Observable<MappedInvestmentDocuments[]> {
    const scenarioStr = scenario ? scenario.toString() : 'main';

    let buildingsToLoad: number[] = [];
    let yearsToLoad: number[] = [];

    return combineLatest(
      this.years.startYear$,
      this.years.endYear$,
      this.perimetersService.perimeters$,
      this.forceReload$,
    ).pipe(
      filter(
        ([startYear, endYear, perimeters]) =>
          perimeters.length > 0
          && (
            !forcedYears && startYear <= endYear
            || forcedYears && forcedYears.length && forcedYears[0] <= forcedYears[1]
          ),
      ),
      tap(() => this.investmentsLoading$.next(true)),
      switchMap(([startYear, endYear, perimeters]) => {
        const buildings = PerimetersService.getBuildingsIds(perimeters);
        buildingsToLoad = [];
        yearsToLoad = [];

        for (
          let year = (forcedYears && forcedYears.length ? forcedYears[0] : startYear);
          year <= (forcedYears && forcedYears.length ? forcedYears[1] : endYear);
          year++
        ) {
          for (const building of buildings) {
            if (
              R.path([scenarioStr, year, building], this.loadedYearBuilding) ===
              undefined
            ) {
              buildingsToLoad.push(building);
              yearsToLoad.push(year);
            }
          }
        }
        return this.loadInvestments(
          R.uniq(yearsToLoad),
          R.uniq(buildingsToLoad),
          scenario,
          assetFields,
        ).pipe(finalize(() => this.investmentsLoading$.next(false)));
      }),
      tap((loadedInvestments) => {
        let path;

        let buildingStr: string;

        let existingInvId: number;

        // Cache the loaded investments
        loadedInvestments.forEach((loadedInvestment) => {
          buildingStr = loadedInvestment.building
            ? loadedInvestment.building.toString()
            : null;

          path = R.path(
            [scenarioStr, buildingStr],
            this.investmentsPerScenarioYearBuilding,
          );

          if (path === undefined) {
            this.investmentsPerScenarioYearBuilding = R.assocPath(
              [scenarioStr, buildingStr],
              [],
              this.investmentsPerScenarioYearBuilding,
            );
          }

          existingInvId = this.investmentsPerScenarioYearBuilding[scenarioStr][
            loadedInvestment.building
          ].findIndex((inv) => inv.id === loadedInvestment.id);

          if (existingInvId === -1) {
            this.investmentsPerScenarioYearBuilding[scenarioStr][
              loadedInvestment.building
            ].push(loadedInvestment);
          }
        });
      }),
      withLatestFrom(
        this.years.startYear$,
        this.years.endYear$,
        this.perimetersService.perimeters$,
      ),
      map(([_loadedInvestments, startYear, endYear, perimeters]) => {
        endYear++;
        // Give the investments requested from the cache
        let mappedInvestments = [];
        let buildingStr: string;

        let newInvestments: MappedInvestmentDocuments[];

        PerimetersService.getBuildingsIds(perimeters).forEach((building) => {
          newInvestments = [];
          buildingStr = building.toString();

          if (
            R.path(
              [scenarioStr, buildingStr],
              this.investmentsPerScenarioYearBuilding,
            )
          ) {
            newInvestments = this.investmentsPerScenarioYearBuilding[
              scenarioStr
            ][buildingStr].filter((inv) => {
              return (
                inv.final_schedule <= (forcedYears && forcedYears.length ? forcedYears[1] : endYear) &&
                inv.final_schedule_to >= (forcedYears && forcedYears.length ? forcedYears[0] : startYear)
              );
            });
          }

          mappedInvestments = R.concat(mappedInvestments, newInvestments);
        });

        return mappedInvestments;
      }),
      tap(() => {
        for (const year of yearsToLoad) {
          const yearStr = year.toString();
          for (const building of buildingsToLoad) {
            const buildingStr = building.toString();
            if (
              R.path(
                [scenarioStr, yearStr, buildingStr],
                this.loadedYearBuilding,
              ) === undefined
            ) {
              this.loadedYearBuilding = R.assocPath(
                [scenarioStr, yearStr, buildingStr],
                true,
                this.loadedYearBuilding,
              );
            }
          }
        }
      }),
      shareReplay(1),
    );
  }

  public loadInvestments(
    years: number[],
    buildings: number[],
    scenario?: number,
    assetFields?: string,
    minimal?: boolean,
    extraParams?: any,
  ): Observable<MappedInvestmentDocuments[] | KPI.Investment[]> {
    // No need to load anything if no building or year to load
    if (buildings.length === 0 || years.length === 0) {
      return of([]);
    }
    return this.loadInvestmentWithParams(
      {
        ...extraParams,
        years: years.join(','),
        buildings: buildings.join(','),
        ...(scenario ? { scenario } : {}),
      },
      assetFields,
      minimal,
    );
  }

  public mapInvestments(
    investments: KPI.Investment[],
    assets: KPI.MappedAsset[],
  ): Observable<MappedInvestmentDocuments[]> {
    return combineLatest([
      this.scope.investmentTypes$,
      this.investmentExecutionService.investmentExecutions$,
      this.investmentReasonsService.investmentReasons$,
      this.documentTypesService.documentTypes$,
      this.scope.perimeters$.pipe(idMapToArray()),
      this.scope.investmentStatuses$,
      this.scope.investmentPriorities$.pipe(idMapToArray()),
      this.investmentCategoriesService.investmentCategoriesOrdered$,
      this.scope.superCategories$,
      this.scope.categories$,
      this.scope.subCategories$,
      this.referenceDataService.perimeterReferenceDataMaps$,
      this.importsService.investmentImportableInvestmentStatuses$,
      this.translate.get('core.utils.investments.no_linked_asset'),
    ]).pipe(
      first(),
      map(
        ([
          investmentTypes,
          investmentExecution,
          investmentReasons,
          documentTypes,
          perimeters,
          statuses,
          priorities,
          investmentCategories,
          superCategories,
          categories,
          subCategories,
          referenceDataPerimeters,
          investmentImportableInvestmentStatuses,
          noLinkedAssetLabel,
        ]) => {
          return investments.map((investment) => {
            const assetObject =
              investment.asset && assets.length
                ? assets.find(
                  (a) => a && investment && a.id === investment.asset,
                )
                : null;
            // Error management when the asset was not found (i.e soft deleted)
            if (investment.asset && !assetObject) {
              const error = new Error(
                `Unable to find asset ${investment.asset} for investment ${investment.id} - ${investment.label}`,
              );
              console.error(error);
              Sentry.captureException(error);
            }
            const invPerimeters = this.findPerimetersInvestments(
              investment,
              perimeters,
              assetObject,
            );
            const multiPerimeter = invPerimeters.find(
              (perimeter) => perimeter.sub_perimeters,
            );
            const multiReferenceDataPerimeter = multiPerimeter
              ? referenceDataPerimeters.find(
                (perimeter) => perimeter.id === multiPerimeter.id,
              )
              : null;

            const subCategory = assetObject
              ? subCategories[assetObject.asset_type_object.parent]
              : subCategories[investment.sub_category];
            const category = subCategory
              ? categories[subCategory.parent]
              : categories[investment.category];
            const superCategory = superCategories[investment.super_category] || (category && superCategories[category.parent]);
            // const importDate = investment.imported_at ? new Date(investment.imported_at) : null;

            let import_statuses_str = null;

            if (investment.import_statuses) {
              const importStatuses = investment.import_statuses.map(
                (status) => {
                  return investmentImportableInvestmentStatuses.find(
                    (stat) => stat.id === status,
                  ).name;
                },
              );
              import_statuses_str = importStatuses.join(', ');
            }

            if (investment.status && (investment.status as any).id) {
              // Sometime status is an object — normalize to ID
              investment.status = (investment.status as any).id;
            }

            return {
              ...investment,
              // FIXME - CH - 2020-03-16 - budget_reference is related to the handsome table event spam issue and
              //  workaround
              // see https://dev.azure.com/kpi-intelligence/KPI%20Intelligence/_workitems/edit/1830
              budget_reference: investment.budget_reference
                ? Number(investment.budget_reference) // Prices come as string (Python Decimal)
                : 0,
              asset_object: assetObject,
              status_object: statuses[investment.status],
              priority_object: priorities?.find(p => p.is_default),
              zone_name: assetObject ? assetObject.zone_object.name : '',
              asset_label: assetObject ? assetObject.label : noLinkedAssetLabel,
              building: assetObject
                ? assetObject.building
                : investment.building,
              perimeters: invPerimeters,
              multi_perimeter: multiReferenceDataPerimeter,
              site_name: invPerimeters.length > 0 ? invPerimeters[0].name : '',
              perimeters_names_list: invPerimeters
                .slice(1)
                .map((perimeter) => perimeter.name)
                .join(' > '),
              investment_type_object:
                investmentTypes[investment.investment_type],
              execution_object: investmentExecution.find(
                (execution) => execution.id === investment.execution,
              ),
              investment_category_object: investmentCategories.find(
                (investment_category) =>
                  investment_category.id === investment.investment_category,
              ),
              super_category: investment.super_category,
              super_category_object: superCategory,
              category: (category || {}).id,
              category_object: category,
              sub_category: (subCategory || {}).id,
              sub_category_object: subCategory,
              // FIXME - CH - 2020-03-16 - imported_at has been removed due to the handsome table event spam issue and
              //  workaround
              // see https://dev.azure.com/kpi-intelligence/KPI%20Intelligence/_workitems/edit/1830
              imported_at_str: investment.imported_at
                ? new Date(investment.imported_at).toLocaleDateString()
                : null,
              import_statuses_str,
              slices: investment.slices.map((slice) => ({
                ...slice,
                price: Number(slice.price), // Prices come as string (Python Decimal)
                additional_price: slice.additional_price
                  ? Number(slice.additional_price)
                  : null,
              })),
              spending_slices: investment.spending_slices.map(
                (spending_slice) => ({
                  ...spending_slice,
                  spent: Number(spending_slice.spent), // Prices come as string (Python Decimal)
                  remaining: Number(spending_slice.remaining), // Prices come as string (Python Decimal)
                }),
              ),
              // Map each InvestmentReason to a boolean stating if it's attached to this investment
              reasons_map: investmentReasons.reduce(
                (reasonsMap, investmentReason) => ({
                  ...reasonsMap,
                  [investmentReason.id]:
                    investment.reasons.findIndex(
                      (reason) => reason === investmentReason.id,
                    ) > -1,
                }),
                {},
              ),
              // Create a mapping for each existing document types
              document_types_map: documentTypes.reduce(
                (documentTypesMap, documentType) => {
                  const investmentDocument = investment.documents?.find(
                    (document) => document.document_type === documentType.id,
                  );
                  if (investmentDocument) {
                    return {
                      ...documentTypesMap,
                      [documentType.id]: {
                        requirement_state: investmentDocument.mandatory
                          ? DocumentTypeRequirementState.MANDATORY
                          : DocumentTypeRequirementState.OPTIONAL,
                        required: investmentDocument.mandatory,
                        has_attached: investmentDocument.count > 0,
                      },
                    };
                  } else {
                    return {
                      ...documentTypesMap,
                      [documentType.id]: {
                        requirement_state: DocumentTypeRequirementState.NO,
                        required: false,
                        has_attached: false,
                      },
                    };
                  }
                },
                {},
              ),
              budget_scopes: investment.budget_scopes,
            };
          });
        },
      ),
    );
  }

  private mapMinimalInvestments(
    investments: KPI.Investment[],
  ): Observable<KPI.Investment[]> {
    return this.scope.investmentStatuses$.pipe(
      first(),
      map((statuses) =>
        investments.map((investment) => ({
          ...investment,
          status_object: statuses[investment.status],
          current_price: InvestmentsService.getInvestmentTotalPrice(investment),
        })),
      ),
    );
  }

  /**
   * Finds the perimeters linked to the investment
   *
   * @param investment
   * @param perimeters
   * @param assets
   */
  private findPerimetersInvestments(
    investment: KPI.Investment | MappedInvestmentDocuments,
    perimeters: KPI.Perimeter[],
    asset_object?: KPI.MappedAsset,
  ): KPI.Perimeter[] {
    let result: KPI.Perimeter[] = [];
    if (asset_object) {
      result = asset_object.perimeters;
    } else {
      let buildingId: number;
      // @ts-ignore
      if (investment.building && investment.building.id) {
        // @ts-ignore
        buildingId = investment.building.id;
      } else {
        buildingId = investment.building;
      }
      result = this.buildingsService.findBuildingPerimeters(
        buildingId,
        perimeters,
      );
    }
    return result;
  }

  public openAuditInvestEditor(configuration: { investmentId: number | string, siteId: number }) {
    const { investmentId, siteId } = configuration
    const popupUrl = `${environment.auditAppURL}/perimeters/${siteId}/investment-detail/${investmentId}/?forceSynthesisReload=true`;
    this.utils.openPopupWindow(popupUrl)
  }

  /**
   * Returns the list of investment statuses that can be reached from the current status,
   * according to the user's groups and status transitions.
   * @param currentStatus
   * @param investmentStatuses
   * @param user
   * @param memberGroups
   */
  public filterInvestmentStatusByTransition(
    currentStatus: KPI.InvestmentStatus,
    investmentStatuses: KPI.InvestmentStatus[],
    user: KPI.User,
    memberGroups: KPI.MembersGroup[]
  ): KPI.InvestmentStatus[] {
    const currentUserGroups = memberGroups.filter((group) =>
      group.members.includes(user.id)
    );

    const allowedTransitions: KPI.InvestmentStatusTransition[] =
      currentUserGroups.reduce(
        (acc, group) =>
          acc.concat(
            group.allowed_investment_status_transitions.filter(
              (transition) => transition.from_status === currentStatus?.id
            )
          ),
        []
      );

    return investmentStatuses.filter((status) => {
      return allowedTransitions.some(
        (transition) => transition.to_status === status.id
      );
    });
  }

  public makeMappedInvestment(data?: any) {
    return combineLatest([
      this.api.investments_investment_statuses(),
      this.api.investments_investment_types(),
      this.api.investments_investment_priorities(),
      this.api.investments_investment_budget_origin(),
      this.api.get_budget_scopes()
    ])
    .pipe(
      map(([investmentStatuses, investmentTypes, investmentPriorities, budgetOrigins, budgetScopes]) => {
        const defaultStatus = investmentStatuses.find(status => status.is_default);
        const defaultType = investmentTypes.find(type => type.is_default) ||
          // If no default type, we try to pick the type "other"
          investmentTypes.filter(type => !type.replacement && !type.renew && !type.maintenance && !type.creation_of_equipment)[0];
        const defaultPriority = investmentPriorities.find(priority => priority.is_default);
        const defaultBudgetOrigin = budgetOrigins.find(origin => origin.is_default);
        const defaultBudgetScope = budgetScopes.find(scope => scope.is_default || scope.is_fourni_pose);

        const investment: KPI.MappedInvestment = {
          asset_object: null,
          investment_type_object: null,
          investment_category_object: null,
          reasons_map: null,
          execution_object: null,
          perimeters: data.perimeters || [],
          multi_perimeter: data.multi_perimeter || null,
          site_name: data.site_name || null,

          id: data.id || 0,
          created_on: data.created_on || new Date().toDateString(),
          label: data.label || "",
          /** Id. of the related asset. */
          // asset: data.asset || null,
          /** Id. of the related building. */
          building: data.building || null,
          /** Id. of the `InvestmentScenario`. */
          scenario: data.scenario || null,
          /** Id. of the `InvestmentStatus`. */
          status: data.status || defaultStatus?.id,

          /** Id. of the `InvestmentType`. */
          investment_type: data.type || defaultType?.id,
          priority: data.priority || defaultPriority?.id,

          quantity: 1,

          /** Budget reference coming from the related asset. */
          budget_reference: null,
          /** Parts of the investment. */
          slices: [],
          /**  For a year the amount of the money spent and the remaining one*/
          spending_slices: [],

          billing_type: null,
          supplier: null,
          sponsorships: [],

          /** Initial scheduled start year. */
          initial_schedule: data.initial_schedule || new Date().getFullYear(),
          /** Initial scheduled end year. */
          initial_schedule_to: data.initial_schedule || new Date().getFullYear(),
          /** Start year in which the investment actually happened. */
          final_schedule: null,
          /** End year in which the investment actually happened. */
          final_schedule_to: null,

          comments: "",
          local_id: getLocalId(),

          taxonomy_objective: null,
          taxonomy_category: null,

          reasons: data.reasons || [],

          duration_deviation: 0,
          lifetime_after_investment: null,

          /** Number of related `Tasks`. */
          nb_tasks: 0,

          documents: [],

          category: null,
          sub_category: null,

          /** True if the investment has no impact on the asset technical state  */
          has_no_impact: true,

          /** Temporary hard-coded additional data. */
          is_capex: data.is_capex || false,
          energy_saving: null,
          investment_category: null,
          /** Id. of `InvestmentExecution` */
          execution: null,
          budget_origin: defaultBudgetOrigin,
          /** minimal initiative */
          initiative: data.initiative || null,
          further_information: null,
          impact_roadmap_indicators: data.impact_roadmap_indicators || [],
          budget_scopes: [defaultBudgetScope.id],

          projects: [],
          imported_at: null,
          import_source: null,
          import_reference: null,
          import_statuses: null,
          perimeters_names_list: '',
          status_object: undefined,
          priority_object: undefined,
          super_category_object: undefined,
          category_object: undefined,
          sub_category_object: undefined,
          imported_at_str: '',
          import_statuses_str: ''
        };
        return investment;
      })
    )
  }

  public createInvestment(investment: Partial<KPI.Investment>): Observable<any> {
    return this.api.investments_create_investment(investment).pipe(
      tap(inv => this.newInvestmentCreated$.next(inv))
    )
  }

  public createComplementaryInvestment(mainInvestmentId: number, investment: Partial<KPI.Investment>): Observable<any> {
    return this.api.investments_create_complementary(mainInvestmentId, investment).pipe(
      tap(inv => this.newInvestmentCreated$.next(inv))
    )
  }

  public getInvestmentBudget(investment: KPI.Investment) {
    const currentSlice = investment.slices.find(slice => slice.status === investment.status);
    return currentSlice?.price || investment.slices[0].price;
  }

  public attachInvestmentAndAsset(investmentId: number, assetId: number) {
    return this.api.investments_attach_investment_and_asset(investmentId, assetId);
  }

  public detachInvestmentAndAsset(investmentId: number, assetId: number) {
    return this.api.investments_detach_investment_and_asset(investmentId, assetId);
  }
}

export function getLocalId(): string {
  let localId: string = "" + Math.round(Date.now()) + "-" + Math.random().toString(36);
  return localId.substr(0, 20);
}
